import React, { useState, useEffect } from 'react';
import { RefreshCw, Trash2 } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from "./card";
import { Input } from "./input";
import { Button } from "./button";
import { Alert, AlertDescription } from "./alert";
import { Checkbox } from "./checkbox";
import { Search } from 'lucide-react';
import { AdminImageSlideshow } from './imageslideshow';
import { PRICES } from '../config/prices';

const API_URL = process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api` : '/api';

const AdminVoucherPanel = () => {
  const [vouchers, setVouchers] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [error, setError] = useState(null);
  const [isUnlimited, setIsUnlimited] = useState(false);
  const [voucherToDelete, setVoucherToDelete] = useState('');
  const [newVoucher, setNewVoucher] = useState({
    discountType: 'percent', // 'percent' or 'amount'
    discount_percent: 20,
    discount_amount_cents: 99, // €0.99
    max_uses: 1,
    valid_days: 30,
    prefix: '',
    random: true
  });
  const [userEmail, setUserEmail] = useState('');
  const [searchEmail, setSearchEmail] = useState('');

  const fetchVouchers = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/admin/vouchers`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (response.ok) {
        setVouchers(data.vouchers || []);
      } else {
        setError(data.error || 'Failed to fetch vouchers');
      }
    } catch (err) {
      setError('Failed to fetch vouchers');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await fetch(`${API_URL}/admin/users`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (response.ok) {
        setUsers(data.users || []);
      } else {
        setError(data.error || 'Failed to fetch users');
      }
    } catch (err) {
      setError('Failed to fetch users');
    } finally {
      setLoadingUsers(false);
    }
  };

  const deleteVoucher = async () => {
    if (!voucherToDelete.trim()) return;

    try {
      const response = await fetch(`${API_URL}/admin/vouchers/${voucherToDelete}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        fetchVouchers();
        setVoucherToDelete('');
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to delete voucher');
      }
    } catch (err) {
      setError('Failed to delete voucher');
    }
  };

  const generateVouchers = async () => {
    try {
      const voucherData = {
        max_uses: newVoucher.max_uses,
        valid_days: isUnlimited ? null : newVoucher.valid_days,
        prefix: newVoucher.prefix,
        random: newVoucher.random
      };

      // Only include the relevant discount field
      if (newVoucher.discountType === 'percent') {
        voucherData.discount_percent = newVoucher.discount_percent;
      } else {
        voucherData.discount_amount_cents = newVoucher.discount_amount_cents;
      }

      const response = await fetch(`${API_URL}/admin/generate-vouchers`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(voucherData)
      });

      const data = await response.json();
      if (response.ok) {
        fetchVouchers();
        setError(null);
      } else {
        setError(data.error || 'Failed to generate vouchers');
      }
    } catch (err) {
      setError('Failed to generate vouchers');
    }
  };

  useEffect(() => {
    fetchVouchers();
    fetchUsers();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchEmail(userEmail);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-2 lg:p-4">
      {/* Voucher List Section */}
      <div className="space-y-2 lg:space-y-4">
        <div className="flex flex-col space-y-2 lg:space-y-4">
          <Button
            onClick={fetchVouchers}
            variant="outline"
            className="flex items-center w-full justify-center"
            disabled={loading}
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh Vouchers
          </Button>

          <div className="flex space-x-2">
            <Input
              placeholder="Enter voucher code to delete"
              value={voucherToDelete}
              onChange={(e) => setVoucherToDelete(e.target.value)}
              className="flex-1"
            />
            <Button
              variant="destructive"
              onClick={deleteVoucher}
              className="flex items-center"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="h-96 overflow-y-auto border rounded-lg bg-background p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {vouchers.map((voucher) => (
              <Card key={voucher._id} className="p-0">
                <CardHeader className="p-0 pb-0">
                  <CardTitle className="font-mono text-sm">{voucher.code}</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="space-y-1 text-xs">
                    <p>
                      {/* For discount type percent or if discountType field does not exist (old codes) use the percent */}
                      Discount: {voucher.discountType === 'percent' || !voucher.discountType
                        ? `${(voucher.discountPercent * 100).toFixed(0)}%`
                        : `€${(voucher.discountAmountCents / 100).toFixed(2)}`}
                    </p>
                    <p>Uses: {voucher.currentUses} / {voucher.maxUses}</p>
                    <p>Valid until: {voucher.expiryDate ? new Date(voucher.expiryDate).toLocaleDateString() : 'Never expires'}</p>
                    <p className={`font-semibold ${voucher.active ? 'text-green-600' : 'text-red-600'}`}>
                      {voucher.active ? 'Active' : 'Inactive'}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}

            {loading && <div className="text-center py-2">Loading...</div>}
            {!loading && vouchers.length === 0 && (
              <div className="text-center py-2 text-gray-500">
                No vouchers found.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create Voucher Section */}
      <div className="space-y-4">
        <h2 className="text-md lg:text-2xl font-bold">Generate New Vouchers</h2>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <Card>
          <CardContent className="pt-2 lg:pt-6">
            <div className="space-y-2 lg:space-y-4">

              <div className="text-sm lg:text-md space-y-2">
                <label>Discount Type</label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="discountType"
                      value="percent"
                      checked={newVoucher.discountType === 'percent'}
                      onChange={(e) => setNewVoucher({
                        ...newVoucher,
                        discountType: e.target.value
                      })}
                    />
                    <span>Percentage (%)</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="discountType"
                      value="amount"
                      checked={newVoucher.discountType === 'amount'}
                      onChange={(e) => setNewVoucher({
                        ...newVoucher,
                        discountType: e.target.value
                      })}
                    />
                    <span>Fixed Amount (€)</span>
                  </label>
                </div>
              </div>

              {newVoucher.discountType === 'percent' && (
                <div className="text-sm lg:text-md space-y-2 lg:space-y-2">
                  <label>Discount Percentage</label>
                  <Input
                    type="number"
                    min="1"
                    max="100"
                    value={newVoucher.discount_percent}
                    onChange={(e) => setNewVoucher({
                      ...newVoucher,
                      discount_percent: parseInt(e.target.value) || 0
                    })}
                    className="w-2/3"
                  />
                </div>
              )}

              {newVoucher.discountType === 'amount' && (
                <div className="text-sm lg:text-md space-y-2 lg:space-y-2">
                  <label>Discount Amount (select from product prices)</label>
                  <div className="space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      {Object.entries(PRICES).map(([size, price]) => (
                        price > 0 && (
                          <button
                            key={size}
                            onClick={() => setNewVoucher({
                              ...newVoucher,
                              discount_amount_cents: Math.round(price * 100)
                            })}
                            className={`p-2 rounded border text-sm transition-colors ${
                              newVoucher.discount_amount_cents === Math.round(price * 100)
                                ? 'border-blue-500 bg-blue-50 font-semibold'
                                : 'border-gray-200 hover:bg-gray-50'
                            }`}
                          >
                            {size}: €{price.toFixed(2)}
                          </button>
                        )
                      ))}
                    </div>
                    <div className="text-xs text-gray-500 pt-2">Or enter custom amount:</div>
                    <div className="flex items-center gap-2 w-2/3">
                      <span>€</span>
                      <Input
                        type="number"
                        min="0.01"
                        step="0.01"
                        value={(newVoucher.discount_amount_cents / 100).toFixed(2)}
                        onChange={(e) => setNewVoucher({
                          ...newVoucher,
                          discount_amount_cents: Math.round(parseFloat(e.target.value) * 100) || 0
                        })}
                      />
                    </div>
                  </div>
                </div>
              )}

              <div className="text-sm lg:text-md space-y-2">
                <label>Maximum Uses Per Voucher</label>
                <Input
                  type="number"
                  min="1"
                  value={newVoucher.max_uses}
                  onChange={(e) => setNewVoucher({
                    ...newVoucher,
                    max_uses: parseInt(e.target.value) || 1
                  })}
                  className="w-2/3"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center space-x-2 mb-2">
                  <Checkbox
                    id="unlimited"
                    checked={isUnlimited}
                    onCheckedChange={setIsUnlimited}
                  />
                  <label className='text-sm lg:text-md' htmlFor="unlimited">Unlimited Validity</label>
                </div>

                {!isUnlimited && (
                  <div className="text-sm lg:text-md space-y-2">
                    <label>Valid Days</label>
                    <Input
                      type="number"
                      min="1"
                      value={newVoucher.valid_days}
                      onChange={(e) => setNewVoucher({
                        ...newVoucher,
                        valid_days: parseInt(e.target.value) || 1
                      })}
                      className="w-2/3"
                    />
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <div className="flex items-center space-x-2 mb-2">
                  <Checkbox
                    id="random"
                    checked={newVoucher.random}
                    onCheckedChange={(checked) => setNewVoucher({
                      ...newVoucher,
                      random: checked
                    })}
                  />
                  <label className='text-sm lg:text-md' htmlFor="random">Random Part After Prefix</label>
                </div>
                <div className="space-y-2">
                  <label className='text-sm lg:text-md'>Prefix</label>
                  <Input
                    type="text"
                    value={newVoucher.prefix}
                    onChange={(e) => setNewVoucher({
                      ...newVoucher,
                      prefix: e.target.value
                    })}
                    placeholder="STRAVA"
                    className="w-2/3 text-sm lg:text-md"
                  />
                </div>
              </div>

              <Button
                onClick={generateVouchers}
                className="w-2/3 text-xs lg:text-md"
              >
                Generate Vouchers
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-2 lg:pt-6 space-y-4">
            <form onSubmit={handleSearch} className="flex gap-2">
              <Input
                type="email"
                placeholder="Enter user email"
                value={userEmail}
                onChange={(e) => setUserEmail(e.target.value)}
                className="flex-1"
              />
              <Button type="submit" className="flex gap-2 items-center">
                <Search className="h-4 w-4" />
                Search
              </Button>
            </form>

            <div className="border-t pt-4">
              <h3 className="text-sm font-medium mb-2">Or select from registered users:</h3>
              {loadingUsers ? (
                <div className="text-center py-2">Loading users...</div>
              ) : (
                <select
                  className="w-full p-2 border rounded-md text-sm"
                  value=""
                  onChange={(e) => {
                    const selectedEmail = e.target.value;
                    if (selectedEmail) {
                      setUserEmail(selectedEmail);
                      setSearchEmail(selectedEmail);
                    }
                  }}
                >
                  <option value="">Select a user ({users.length} total)</option>
                  {users.map((user) => (
                    <option key={user._id} value={user.email}>
                      {user.email} - Last login: {user.last_login_formatted} ({user.num_logins || 0} logins)
                    </option>
                  ))}
                </select>
              )}
            </div>

            {searchEmail && (
              <AdminImageSlideshow userEmail={searchEmail} />
            )}

          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminVoucherPanel;